import { openDB } from 'idb';

// ============================================
// Configuration
// ============================================
const DB_NAME = 'FitnessImageCache';
const STORE_NAME = 'images';
const MAX_CACHE_SIZE_MB = 50;
const EXPIRATION_DAYS = 2;

// ============================================
// Types
// ============================================
interface CachedImage {
  challengeId: string;
  imageId: string;
  blob: Blob;
  cacheTimestamp: number;      // When we cached it
  imageTimestamp: string;       // Original timestamp from API
  size: number;
}

// ============================================
// Database Setup
// ============================================
const getDB = async () => {
  return openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'challengeId' });
        store.createIndex('cacheTimestamp', 'cacheTimestamp');
      }
    },
  });
};

// ============================================
// Helper Functions
// ============================================

// Convert base64 to Blob
const base64ToBlob = (base64: string): Blob => {
  const base64Data = base64.includes('base64,') 
    ? base64.split('base64,')[1] 
    : base64;
  
  const byteCharacters = atob(base64Data);
  const byteArray = new Uint8Array(byteCharacters.length);
  
  for (let i = 0; i < byteCharacters.length; i++) {
    byteArray[i] = byteCharacters.charCodeAt(i);
  }
  
  return new Blob([byteArray], { type: 'image/jpeg' });
};

// Check if image is expired (based on cache time)
const isExpired = (cacheTimestamp: number): boolean => {
  const expirationTime = EXPIRATION_DAYS * 24 * 60 * 60 * 1000;
  return Date.now() - cacheTimestamp > expirationTime;
};

// Get total cache size in MB
const getCacheSize = async (): Promise<number> => {
  const db = await getDB();
  const allImages = await db.getAll(STORE_NAME);
  const totalBytes = allImages.reduce((sum, img) => sum + (img.size || 0), 0);
  return totalBytes / (1024 * 1024); // Convert to MB
};

// ============================================
// Main Cache Functions
// ============================================

/**
 * Save an image to cache
 * @param challengeId - Unique challenge identifier
 * @param imageId - Image version/uid
 * @param base64 - Base64 image data
 * @param imageTimestamp - Timestamp from API response
 */
export const saveImageToCache = async (
  challengeId: string,
  imageId: string,
  base64: string,
  imageTimestamp: string
): Promise<void> => {
  try {
    const db = await getDB();
    const blob = base64ToBlob(base64);
    
    const entry: CachedImage = {
      challengeId,
      imageId,
      blob,
      cacheTimestamp: Date.now(),
      imageTimestamp,
      size: blob.size,
    };
    
    // Check if we need to free up space
    const currentSize = await getCacheSize();
    if (currentSize > MAX_CACHE_SIZE_MB) {
      await cleanOldestImages(5); // Remove 5 oldest images
    }
    
    await db.put(STORE_NAME, entry);
  } catch (error) {
    console.error('Error saving image to cache:', error);
  }
};

/**
 * Get image URL from cache if timestamp matches
 * @param challengeId - Unique challenge identifier
 * @param apiTimestamp - Current timestamp from API (optional)
 * @returns Object URL or null if not found/timestamp mismatch
 */
export const getImageFromCache = async (
  challengeId: string,
  apiTimestamp?: string
): Promise<string | null> => {
  try {
    const db = await getDB();
    const cached = await db.get(STORE_NAME, challengeId) as CachedImage | undefined;
    
    if (!cached) return null;
    
    // Check if expired (2 days old)
    if (isExpired(cached.cacheTimestamp)) {
      await db.delete(STORE_NAME, challengeId);
      return null;
    }
    
    // Check if timestamp changed (image updated on server)
    if (apiTimestamp && cached.imageTimestamp !== apiTimestamp) {
      await db.delete(STORE_NAME, challengeId);
      return null;
    }
    
    // Create object URL from blob
    const objectUrl = URL.createObjectURL(cached.blob);
    return objectUrl;
  } catch (error) {
    console.error('Error getting image from cache:', error);
    return null;
  }
};

/**
 * Save multiple images to cache (batch operation)
 * @param images - Array of images to cache
 */
export const saveMultipleImages = async (
  images: Array<{ challengeId: string; imageId: string; base64: string; imageTimestamp: string }>
): Promise<void> => {
  try {
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    
    for (const img of images) {
      const blob = base64ToBlob(img.base64);
      const entry: CachedImage = {
        challengeId: img.challengeId,
        imageId: img.imageId,
        blob,
        cacheTimestamp: Date.now(),
        imageTimestamp: img.imageTimestamp,
        size: blob.size,
      };
      await tx.store.put(entry);
    }
    
    await tx.done;
  } catch (error) {
    console.error('Error saving multiple images:', error);
  }
};

/**
 * Clear all cached images
 */
export const clearAllCache = async (): Promise<void> => {
  try {
    const db = await getDB();
    await db.clear(STORE_NAME);
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
};

/**
 * Remove oldest images to free up space
 * @param count - Number of images to remove
 */
const cleanOldestImages = async (count: number): Promise<void> => {
  try {
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const index = tx.store.index('cacheTimestamp');
    
    let cursor = await index.openCursor();
    let removed = 0;
    
    while (cursor && removed < count) {
      await cursor.delete();
      removed++;
      cursor = await cursor.continue();
    }
    
    await tx.done;
  } catch (error) {
    console.error('Error cleaning old images:', error);
  }
};

/**
 * Check if image exists in cache and is valid
 * @param challengeId - Unique challenge identifier
 * @param apiTimestamp - Current timestamp from API (optional)
 * @returns true if cached and valid
 */
export const isCached = async (
  challengeId: string,
  apiTimestamp?: string
): Promise<boolean> => {
  try {
    const db = await getDB();
    const cached = await db.get(STORE_NAME, challengeId) as CachedImage | undefined;
    
    if (!cached) return false;
    
    // Check if expired
    if (isExpired(cached.cacheTimestamp)) return false;
    
    // Check if timestamp matches
    if (apiTimestamp && cached.imageTimestamp !== apiTimestamp) return false;
    
    return true;
  } catch (error) {
    console.error('Error checking cache:', error);
    return false;
  }
};

/**
 * Get cache statistics
 */
export const getCacheStats = async () => {
  try {
    const db = await getDB();
    const allImages = await db.getAll(STORE_NAME);
    const totalSize = allImages.reduce((sum, img) => sum + (img.size || 0), 0);
    
    return {
      totalImages: allImages.length,
      totalSizeMB: (totalSize / (1024 * 1024)).toFixed(2),
      maxSizeMB: MAX_CACHE_SIZE_MB,
    };
  } catch (error) {
    console.error('Error getting cache stats:', error);
    return { totalImages: 0, totalSizeMB: '0', maxSizeMB: MAX_CACHE_SIZE_MB };
  }
};